import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Light, modern, professional palette
const colors = {
  primary: {
    main: '#00695c',      // Deep teal - professional, trustworthy
    light: '#439889',
    dark: '#003d33',
    contrastText: '#fff',
  },
  secondary: {
    main: '#ff8f00',      // Warm amber - approachable, helpful
    light: '#ffc046',
    dark: '#c56000',
    contrastText: '#fff',
  },
  success: {
    main: '#2e7d32',
    light: '#60ad5e',
    dark: '#005005',
  },
  error: {
    main: '#d32f2f',
    light: '#ff6659',
    dark: '#9a0007',
  },
  warning: {
    main: '#ed6c02',
    light: '#ff9d3f',
    dark: '#b53d00',
  },
  info: {
    main: '#0277bd',
    light: '#58a5f0',
    dark: '#004c8c',
  },
  text: {
    primary: '#263238',     // Dark slate - great readability
    secondary: '#546e7a',   // Muted blue-grey - professional
    disabled: '#90a4ae',
  },
  background: {
    default: '#fafafa',     // Very light grey - clean, professional
    paper: '#ffffff',
    alternate: '#f5f7f9',   // Subtle cool tint for sections
  },
  divider: 'rgba(0, 0, 0, 0.09)',
};

// Professional typography settings
const typography = {
  fontFamily: '"Roboto", "Open Sans", "Helvetica", "Arial", sans-serif',
  h1: {
    fontWeight: 700,
    fontSize: '2.5rem',
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h2: {
    fontWeight: 700,
    fontSize: '2.25rem',
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontWeight: 600,
    fontSize: '1.875rem',
    lineHeight: 1.3,
    letterSpacing: '0',
  },
  h4: {
    fontWeight: 600,
    fontSize: '1.5rem',
    lineHeight: 1.4,
    letterSpacing: '0',
  },
  h5: {
    fontWeight: 600,
    fontSize: '1.25rem',
    lineHeight: 1.4,
    letterSpacing: '0',
  },
  h6: {
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.5,
    letterSpacing: '0.01em',
  },
  subtitle1: {
    fontSize: '1.125rem',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
  },
  button: {
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'none',
    letterSpacing: '0.01em',
  },
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
  },
};

// Enhanced component overrides
const components = {
  MuiAppBar: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: '10px 20px',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          transform: 'translateY(-2px)',
        },
        transition: 'all 0.2s ease-in-out',
      },
      contained: {
        '&:hover': {
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.18)',
        },
      },
      outlined: {
        borderWidth: 1.5,
      },
    },
  },
  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: 12,
      },
      elevation1: {
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
      },
      elevation2: {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
      },
      elevation3: {
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
          },
        },
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: 'rgba(0, 0, 0, 0.08)',
      },
    },
  },
  MuiContainer: {
    styleOverrides: {
      root: {
        paddingLeft: 24,
        paddingRight: 24,
        '@media (min-width: 600px)': {
          paddingLeft: 32,
          paddingRight: 32,
        },
      },
    },
  },
};

// Create the theme with our configurations
let theme = createTheme({
  palette: colors,
  typography: typography,
  components: components,
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 2px 8px rgba(0, 0, 0, 0.06)',
    '0 4px 16px rgba(0, 0, 0, 0.08)',
    '0 6px 20px rgba(0, 0, 0, 0.1)',
    '0 8px 24px rgba(0, 0, 0, 0.12)',
    // ...rest of shadows
  ],
  spacing: 8, // Base spacing unit of 8px
});

// Make typography responsive
theme = responsiveFontSizes(theme);

export default theme;
